import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { UsageAndLimitsSection } from "../components/UsageAndLimitsSection";
import {
  getLawyerProfile,
  getCasesByLawyer,
  getPendingConsultations,
  getClientsByLawyer,
  getCaseStats,
  getActiveCasesCount,
  getPendingConsultationsCount,
  getDocumentCount,
  getDocumentCountThisMonth,
} from "@/shared/db";
import { CaseCard } from "../components/CaseCard";
import { ConsultationCard } from "../components/ConsultationCard";
import { ClientCard } from "../components/ClientCard";
import { RecentReviewsWidget } from "../../document-review/components/RecentReviewsWidget";
import type { CaseWithDetails } from "../types/case.types";
import {
  PlusCircleIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  ChartBarIcon,
  ClockIcon,
  ArrowRightIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { ROUTES } from "@/shared/core";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button, ConfirmModal } from "@/shared/ui";
import { DashboardSkeleton } from "../../../components/skeletons";

export const LawyerDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { subscription, limits, getUsage } = usePlanLimits();

  const formatPlanName = (planType: string | null): string => {
    if (!planType) return "Free";
    return planType
      .replace(/^lawyer_/, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const [lawyerProfile, setLawyerProfile] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [activeCases, setActiveCases] = useState<CaseWithDetails[]>([]);
  const [pendingConsultations, setPendingConsultations] = useState<
    Record<string, unknown>[]
  >([]);
  const [clients, setClients] = useState<Record<string, unknown>[]>([]);
  const [stats, setStats] = useState({
    totalCases: 0,
    activeCases: 0,
    pendingConsultations: 0,
    totalClients: 0,
    totalDocuments: 0,
    documentsThisMonth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showProfileCompletenessModal, setShowProfileCompletenessModal] =
    useState(false);
  const [missingProfileFields, setMissingProfileFields] = useState<string[]>(
    [],
  );

  const getMissingProfileFields = useCallback(
    (
      fullName: string | null | undefined,
      profileData: Record<string, unknown> | null,
    ): string[] => {
      if (!profileData) return [];

      const missing: string[] = [];
      const university =
        typeof profileData.university === "string"
          ? profileData.university
          : "";
      const practiceAreas = Array.isArray(profileData.practice_areas)
        ? profileData.practice_areas
        : [];
      const bio = typeof profileData.bio === "string" ? profileData.bio : "";
      const yearsOfExperience =
        typeof profileData.years_of_experience === "number"
          ? profileData.years_of_experience
          : null;
      const hourlyRate =
        typeof profileData.hourly_rate === "number"
          ? profileData.hourly_rate
          : null;

      if (!fullName?.trim()) {
        missing.push(
          t("lawyer.dashboard.profileCompletenessModal.fields.fullName"),
        );
      }

      if (!university.trim()) {
        missing.push(
          t("lawyer.dashboard.profileCompletenessModal.fields.university"),
        );
      }

      if (practiceAreas.length === 0) {
        missing.push(
          t("lawyer.dashboard.profileCompletenessModal.fields.practiceAreas"),
        );
      }

      if (yearsOfExperience === null || yearsOfExperience < 0) {
        missing.push(
          t("lawyer.dashboard.profileCompletenessModal.fields.experience"),
        );
      }

      if (hourlyRate !== null && hourlyRate <= 0) {
        missing.push(
          t("lawyer.dashboard.profileCompletenessModal.fields.hourlyRate"),
        );
      }

      if (!bio.trim()) {
        missing.push(t("lawyer.dashboard.profileCompletenessModal.fields.bio"));
      }

      return missing;
    },
    [t],
  );

  useEffect(() => {
    if (loading || !user || !lawyerProfile) return;

    const sessionKey = `lawyer-profile-completeness-modal:${user.id}`;
    if (sessionStorage.getItem(sessionKey) === "dismissed") return;

    const missing = getMissingProfileFields(profile?.full_name, lawyerProfile);
    if (missing.length > 0) {
      setMissingProfileFields(missing);
      setShowProfileCompletenessModal(true);
    }
  }, [
    loading,
    user,
    lawyerProfile,
    profile?.full_name,
    getMissingProfileFields,
  ]);

  useEffect(() => {
    if (user) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load lawyer profile
      const profileData = await getLawyerProfile(user.id);
      setLawyerProfile(profileData);

      // Load active cases (limit 5)
      const casesData = await getCasesByLawyer(user.id, {
        status: "active",
        limit: 5,
      });

      // Transform cases to match CaseWithDetails type
      const transformedCases: CaseWithDetails[] = (casesData || []).map(
        (case_: unknown) => {
          const caseData = case_ as Record<string, unknown>;
          return {
            ...caseData,
            description: caseData.description ?? undefined,
            notes: Array.isArray(caseData.notes)
              ? caseData.notes.filter(
                  (
                    note: unknown,
                  ): note is {
                    id: string;
                    content: string;
                    author_id: string;
                    author_name: string;
                    timestamp: string;
                  } =>
                    typeof note === "object" &&
                    note !== null &&
                    "id" in note &&
                    "content" in note &&
                    "author_id" in note &&
                    "author_name" in note &&
                    "timestamp" in note,
                )
              : undefined,
          } as CaseWithDetails;
        },
      );

      setActiveCases(transformedCases);

      // Load pending consultations (limit 5)
      const consultationsData = await getPendingConsultations(user.id);
      setPendingConsultations(consultationsData.slice(0, 5));

      // Load clients (limit 4)
      const clientsData = await getClientsByLawyer(user.id);
      setClients(clientsData.slice(0, 4));

      // Load statistics
      const caseStatsData = await getCaseStats(user.id);
      const activeCasesCount = await getActiveCasesCount(user.id);
      const pendingConsCount = await getPendingConsultationsCount(user.id);
      const totalDocuments = await getDocumentCount(user.id);
      const documentsThisMonth = await getDocumentCountThisMonth(user.id);

      setStats({
        totalCases: caseStatsData.total,
        activeCases: activeCasesCount,
        pendingConsultations: pendingConsCount,
        totalClients: clientsData.length,
        totalDocuments,
        documentsThisMonth,
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      toast.error(t("lawyer.dashboard.error"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardSkeleton
        statsCount={4}
        className="bg-linear-to-br from-romance-50 via-white to-romance-100"
        showQuickActions={false}
      />
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <ConfirmModal
        isOpen={showProfileCompletenessModal}
        onClose={() => {
          if (user) {
            sessionStorage.setItem(
              `lawyer-profile-completeness-modal:${user.id}`,
              "dismissed",
            );
          }
          setShowProfileCompletenessModal(false);
        }}
        onConfirm={() => {
          if (user) {
            sessionStorage.setItem(
              `lawyer-profile-completeness-modal:${user.id}`,
              "dismissed",
            );
          }
          setShowProfileCompletenessModal(false);
          navigate("/dashboard/lawyer/profile/edit");
        }}
        title={t("lawyer.dashboard.profileCompletenessModal.title")}
        message={t("lawyer.dashboard.profileCompletenessModal.message", {
          missingFields: missingProfileFields.slice(0, 3).join(", "),
        })}
        confirmText={t("lawyer.dashboard.profileCompletenessModal.confirm")}
        cancelText={t("lawyer.dashboard.profileCompletenessModal.cancel")}
        variant="warning"
      />
      <div className="mx-auto max-w-full min-w-0">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-tiber-900 mb-2 wrap-break-word">
                {t("lawyer.dashboard.title", {
                  name: profile?.full_name,
                })}
              </h1>
              <p className="mt-2 text-sm sm:text-base text-tiber-700">
                {t("lawyer.dashboard.subtitle")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
              {Boolean(lawyerProfile?.is_verified) && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center px-4 py-2 bg-linear-to-r from-atlantis-500 to-atlantis-700 text-white rounded-full text-sm font-semibold shadow-lg"
                >
                  <CheckBadgeIcon className="h-5 w-5 mr-2" />
                  {t("lawyer.dashboard.verified")}
                </motion.div>
              )}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center px-4 py-2 bg-linear-to-r from-dorado to-dorado-dark text-white rounded-full text-sm font-semibold shadow-lg"
              >
                <SparklesIcon className="h-5 w-5 mr-2" />
                {formatPlanName(subscription?.planType || profile?.plan)}
              </motion.div>
              <Button
                variant="primary"
                onClick={() => navigate(ROUTES.GENERATE)}
                leftIcon={<PlusCircleIcon className="w-5 h-5" />}
              >
                {t("lawyer.dashboard.quickActions.generateDocument")}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-linear-to-br from-tiber-500 to-tiber-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer"
            onClick={() => navigate("/dashboard/lawyer/cases")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-30 backdrop-blur-sm rounded-lg shadow-md">
                <BriefcaseIcon className="w-8 h-8 text-tiber-900 drop-shadow-lg" />
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white opacity-90" />
            </div>
            <div>
              <p className="text-white/90 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.totalCases")}
              </p>
              <p className="text-3xl font-bold text-white">
                {stats.totalCases}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-linear-to-br from-atlantis-500 to-atlantis-700 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer"
            onClick={() => navigate("/dashboard/lawyer/cases")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-30 backdrop-blur-sm rounded-lg shadow-md">
                <DocumentTextIcon className="w-8 h-8 text-tiber-900 drop-shadow-lg" />
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white opacity-90" />
            </div>
            <div>
              <p className="text-white/90 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.activeCases")}
              </p>
              <p className="text-3xl font-bold text-white">
                {stats.activeCases}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-linear-to-br from-dorado to-dorado-dark rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer"
            onClick={() => navigate("/dashboard/lawyer/consultations")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-30 backdrop-blur-sm rounded-lg shadow-md">
                <ClockIcon className="w-8 h-8 text-tiber-900 drop-shadow-lg" />
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white opacity-90" />
            </div>
            <div>
              <p className="text-white/90 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.pendingConsultations")}
              </p>
              <p className="text-3xl font-bold text-white">
                {stats.pendingConsultations}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-linear-to-br from-tiber-400 to-tiber-600 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer"
            onClick={() => navigate("/dashboard/lawyer/clients")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-30 backdrop-blur-sm rounded-lg shadow-md">
                <UserGroupIcon className="w-8 h-8 text-tiber-900 drop-shadow-lg" />
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white opacity-90" />
            </div>
            <div>
              <p className="text-white/90 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.clients")}
              </p>
              <p className="text-3xl font-bold text-white">
                {stats.totalClients}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Additional Stats - Documents */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-xl transition-all cursor-pointer"
            onClick={() => navigate("/dashboard/lawyer/documents")}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-linear-to-br from-tiber-400 to-tiber-600 rounded-lg">
                <DocumentTextIcon className="w-6 h-6 text-white" />
              </div>
              <ArrowRightIcon className="w-5 h-5 text-tiber-400" />
            </div>
            <div>
              <p className="text-tiber-600 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.totalDocuments")}
              </p>
              <p className="text-3xl font-bold text-tiber-900">
                {stats.totalDocuments}
              </p>
              <p className="text-sm text-tiber-500 mt-2">
                {stats.documentsThisMonth}{" "}
                {t("lawyer.dashboard.stats.thisMonth")}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600 hover:shadow-xl transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-linear-to-br from-tiber-500 to-atlantis-600 rounded-lg">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <p className="text-tiber-600 text-sm font-medium mb-1">
                {t("lawyer.dashboard.stats.performance")}
              </p>
              <p className="text-2xl font-bold text-tiber-900">
                {stats.activeCases > 0
                  ? Math.round((stats.activeCases / stats.totalCases) * 100)
                  : 0}
                %
              </p>
              <p className="text-sm text-tiber-500 mt-2">
                {t("lawyer.dashboard.stats.casesActive")}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-linear-to-r from-tiber-400 to-tiber-600 rounded-xl p-6 mb-8 shadow-lg"
        >
          <h3 className="text-white text-xl font-bold mb-4">
            {t("lawyer.dashboard.quickActions.title")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(ROUTES.GENERATE)}
              className="flex items-center justify-center space-x-3 bg-white text-tiber-900 cursor-pointer px-6 py-4 rounded-lg hover:bg-romance-50 transition-all font-semibold shadow-md"
            >
              <DocumentTextIcon className="h-6 w-6" />
              <span>{t("lawyer.dashboard.quickActions.generateDocument")}</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/dashboard/lawyer/cases", { state: { openCreateModal: true } })}
              className="flex items-center justify-center space-x-3 bg-white text-tiber-900 cursor-pointer px-6 py-4 rounded-lg hover:bg-romance-50 transition-all font-semibold shadow-md"
            >
              <PlusCircleIcon className="h-6 w-6" />
              <span>{t("lawyer.dashboard.quickActions.newCase")}</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/dashboard/lawyer/profile/edit")}
              className="flex items-center justify-center space-x-3 bg-white text-tiber-900 cursor-pointer px-6 py-4 rounded-lg hover:bg-romance-50 transition-all font-semibold shadow-md"
            >
              <UserGroupIcon className="h-6 w-6" />
              <span>{t("lawyer.dashboard.quickActions.editProfile")}</span>
            </motion.button>
          </div>
        </motion.div> */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Active Cases */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-linear-to-br from-atlantis-500 to-atlantis-700 rounded-lg">
                  <BriefcaseIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-tiber-900">
                  {t("lawyer.dashboard.sections.activeCases.title")}
                </h2>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard/lawyer/cases")}
                rightIcon={
                  <ArrowRightIcon className="w-4 h-4 text-tiber-900" />
                }
              >
                {t("lawyer.dashboard.sections.activeCases.viewAll")}
              </Button>
            </div>
            <div className="space-y-3">
              {activeCases.length > 0 ? (
                activeCases.map((case_) => (
                  <CaseCard
                    key={case_.id}
                    case={case_}
                    onClick={() =>
                      navigate(`/dashboard/lawyer/cases/${case_.id}`)
                    }
                  />
                ))
              ) : (
                <div className="rounded-lg p-8 text-center">
                  <BriefcaseIcon className="h-12 w-12 text-tiber-600 mx-auto mb-3" />
                  <p className="text-tiber-600 font-medium mb-2">
                    {t("lawyer.dashboard.sections.activeCases.empty")}
                  </p>
                  <Button
                    onClick={() =>
                      navigate("/dashboard/lawyer/cases", {
                        state: { openCreateModal: true },
                      })
                    }
                    variant="ghost"
                    rightIcon={
                      <ArrowRightIcon className="w-4 h-4 text-tiber-900" />
                    }
                  >
                    {t("lawyer.dashboard.sections.activeCases.createFirst")}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Pending Consultations */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-linear-to-br from-dorado to-dorado-dark rounded-lg">
                  <ClockIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-tiber-900">
                  {t("lawyer.dashboard.sections.pendingConsultations.title")}
                </h2>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard/lawyer/consultations")}
                rightIcon={
                  <ArrowRightIcon className="w-4 h-4 text-tiber-900" />
                }
              >
                {t("lawyer.dashboard.sections.pendingConsultations.viewAll")}
              </Button>
            </div>
            <div className="space-y-3">
              {pendingConsultations.length > 0 ? (
                pendingConsultations.map((consultation) => (
                  <ConsultationCard
                    key={String(consultation.id || "")}
                    consultation={
                      consultation as unknown as Parameters<
                        typeof ConsultationCard
                      >[0]["consultation"]
                    }
                    onAccept={async () => {
                      toast.success(
                        t(
                          "lawyer.dashboard.sections.pendingConsultations.accepted",
                        ),
                      );
                      await loadDashboardData();
                    }}
                    onReject={async () => {
                      toast.success(
                        t(
                          "lawyer.dashboard.sections.pendingConsultations.rejected",
                        ),
                      );
                      await loadDashboardData();
                    }}
                  />
                ))
              ) : (
                <div className="p-8 text-center">
                  <DocumentTextIcon className="h-12 w-12 text-tiber-600 mx-auto mb-3" />
                  <p className="text-tiber-600 font-medium">
                    {t("lawyer.dashboard.sections.pendingConsultations.empty")}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Document Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-8"
        >
          <RecentReviewsWidget basePath="/dashboard/lawyer" limit={3} />
        </motion.div>

        {/* Uso y Límites */}
        <UsageAndLimitsSection
          limits={limits}
          getUsage={getUsage}
          formatPlanName={formatPlanName}
          planType={subscription?.planType ?? null}
        />

        {/* Clients Section */}
        {clients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-white rounded-xl p-6 shadow-lg border border-tiber-600"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-linear-to-br from-tiber-500 to-tiber-600 rounded-lg">
                  <UserGroupIcon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-tiber-900">
                  {t("lawyer.dashboard.sections.recentClients.title")}
                </h2>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard/lawyer/clients")}
              >
                {t("lawyer.dashboard.sections.recentClients.viewAll")}
                <ArrowRightIcon className="w-4 h-4 text-tiber-900" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <ClientCard
                  key={String(client.id || "")}
                  client={client as Parameters<typeof ClientCard>[0]["client"]}
                  onClick={() =>
                    navigate(
                      `/dashboard/lawyer/clients/${String(client.id || "")}`,
                    )
                  }
                  showStats={true}
                />
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
